import type {
  AddFolderResponse,
  DeleteResponse,
  FolderResponse,
  MoveResponse,
} from '@/types/folders/folder';
import type { ApiResponse } from '@/types';
import { httpServices } from './client';
import {
  mockDeleteFolder,
  mockMoveFolder,
  mockRenameFolder,
} from '@/services/mock/documents/services/mockFolderService';

/** Guideline・Context folders (`/llm/folder/*`). */

// Never in production builds.
const USE_MOCK =
  import.meta.env.MODE !== 'prod' &&
  import.meta.env.VITE_USE_MOCK_DB === 'true';

/** Without `folder_id`, the root listing. */
export interface GetFolderParams {
  folder_id?: string;
  project_id?: null;
}

export interface CreateFolderParams {
  folder_name: string;
  parent_id?: string;
}

export interface DeleteFolderParams {
  folder_id: string;
}

export interface RenameFolderParams {
  folder_id: string;
  folder_name: string;
}

/** An empty `folder_id_to` moves the folder to the root. */
export interface MoveFolderParams {
  folder_id: string;
  folder_id_to: string;
}

export interface FolderDescendants {
  folder_ids: string[];
  file_ids: string[];
}

export interface FolderDescendantsBatchResponse {
  success: boolean;
  data?: {
    descendants?: Record<string, FolderDescendants>;
  };
  error?: string;
}

/** Same envelope as a file rename; a folder has no `path`. */
export type RenameFolderResponse = ApiResponse<{ path?: string }>;

/** GET /llm/folder[?folder_id=] */
export const getFolder = (params: GetFolderParams) =>
  httpServices.llmService.get<FolderResponse>('/folder', params);

/**
 * POST /llm/folder/descendants: resolve the descendants of several folders
 * in one request, which lets a caller derive the selection state of a
 * whole folder listing without a request per folder.
 */
export const getFolderDescendantsBatch = (folderIds: string[]) =>
  httpServices.llmService.post<FolderDescendantsBatchResponse>(
    '/folder/descendants',
    { folder_ids: folderIds },
  );

/** POST /llm/folder?folder_name=&parent_id= (empty body) */
export const createFolder = (params: CreateFolderParams) =>
  httpServices.llmService.post<AddFolderResponse>('/folder', {}, params);

/** DELETE /llm/folder?folder_id= */
export const deleteFolder = async (params: DeleteFolderParams) => {
  if (USE_MOCK) {
    return mockDeleteFolder(params);
  }
  return httpServices.llmService.delete<DeleteResponse>(`/folder`, params);
};

/** POST /llm/folder/rename?folder_id=&folder_name= (empty body) */
export const renameFolder = async (
  params: RenameFolderParams,
): Promise<RenameFolderResponse> => {
  if (USE_MOCK) {
    // The in-browser mock answers with the folder instead.
    return (await mockRenameFolder(params)) as unknown as RenameFolderResponse;
  }
  return httpServices.llmService.post<RenameFolderResponse>(
    `/folder/rename`,
    {},
    params,
  );
};

/** POST /llm/folder/move?folder_id=&folder_id_to= (empty body) */
export const moveFolder = async (params: MoveFolderParams) => {
  if (USE_MOCK) {
    return mockMoveFolder(params);
  }
  return httpServices.llmService.post<MoveResponse>(`/folder/move`, {}, params);
};
